/**
 * CommonTable – reusable wrapper for page-level tables.
 *
 * - Does not fetch data; parent supplies table content (Table + TableHead + TableBody) and pagination state.
 * - Provides consistent chrome: Paper, optional title/headerAction, loading/empty states,
 *   TableContainer styling, and TablePagination.
 *
 * Use:
 *   <CommonTable count={total} page={page} rowsPerPage={rowsPerPage} onPageChange={...} onRowsPerPageChange={...}>
 *     <Table stickyHeader size="small">
 *       <TableHead>...</TableHead>
 *       <TableBody>...</TableBody>
 *     </Table>
 *   </CommonTable>
 */

import React, { ReactNode } from 'react';
import {
  Box,
  Paper,
  Typography,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  alpha,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { SEARCE_COLORS } from '../../theme';
import { TABLE_STYLES } from '../../constants/tableStyles';
import { TableLoadingOverlay, EmptyState } from './loading';

export interface CommonTableProps {
  /** Table content (e.g. <Table><TableHead>...</TableHead><TableBody>...</TableBody></Table>). */
  children: ReactNode;

  /** Table title above the table (e.g. "Employee details"). */
  title?: string;

  /** Right side of header (e.g. row count chip, "Clear filters" button). */
  headerAction?: ReactNode;

  /** When true, show loading overlay and hide children. */
  loading?: boolean;

  /** When true, show empty state message instead of children. */
  empty?: boolean;

  /** Message when empty. Default: "No data available". */
  emptyMessage?: string;

  /** Total row count for TablePagination. */
  count: number;

  /** Current 0-based page. */
  page: number;

  /** Rows per page. */
  rowsPerPage: number;

  /** Pagination page change handler. */
  onPageChange: (event: unknown, newPage: number) => void;

  /** Rows-per-page change handler. */
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;

  /** Rows per page options. Default: [5, 10, 25, 50]. */
  rowsPerPageOptions?: number[];

  /** When false, hide pagination bar. Default: true. */
  showPagination?: boolean;

  /** TableContainer maxHeight. Default: 600. */
  maxHeight?: string | number;

  /** Paper elevation. Default: 2. */
  elevation?: number;

  /** Slightly reduce padding. Default: false. */
  dense?: boolean;

  /** Optional color overrides. When not passed, defaults use theme (SEARCE_COLORS). Parent can use tableHeader* for their TableHead cells. */
  colors?: {
    /** Title/header row background. Default: transparent. */
    headerBackground?: string;
    /** Title and header row text. Default: SEARCE_COLORS.textPrimary. */
    headerText?: string;
    /** Table header row background (use in parent's TableHead). Default: SEARCE_COLORS.primary. */
    tableHeaderBackground?: string;
    /** Table header row text (use in parent's TableHead). Default: SEARCE_COLORS.white. */
    tableHeaderText?: string;
    /** Pagination label/displayed rows text. Default: SEARCE_COLORS.textSecondary. */
    paginationText?: string;
    /** Pagination select and icon buttons. Default: SEARCE_COLORS.primary. */
    paginationActive?: string;
  };
  /** Optional sx for the outer Paper wrapper. */
  paperSx?: SxProps<Theme>;
  /** Optional sx for TableContainer. */
  tableContainerSx?: SxProps<Theme>;
  /** Optional sx merged into TablePagination (e.g. remove borderTop when flush with table). */
  paginationSx?: SxProps<Theme>;
}

const DEFAULT_EMPTY_MESSAGE = 'No data found';
const DEFAULT_ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

const CommonTable: React.FC<CommonTableProps> = ({
  children,
  title,
  headerAction,
  loading = false,
  empty = false,
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  showPagination = true,
  maxHeight = 600,
  elevation = 2,
  dense = false,
  colors,
  paperSx,
  tableContainerSx,
  paginationSx,
}) => {
  const showHeader = title != null || headerAction != null;
  const showTableContent = !loading && !empty;

  const headerBg = colors?.headerBackground;
  const headerText = colors?.headerText ?? SEARCE_COLORS.textPrimary;
  const paginationText = colors?.paginationText ?? SEARCE_COLORS.textSecondary;
  const paginationActive = colors?.paginationActive ?? SEARCE_COLORS.primary;

  const pad = dense ? 1.5 : 2;

  /** White panel, padding, light border; flex column so the scroll region can use remaining height/width. */
  const paperBaseSx = {
    ...(showHeader ? { px: pad, pb: pad, pt: 0 } : { p: pad }),
    mb: 1,
    borderRadius: 2,
    border: `1px solid ${SEARCE_COLORS.border}`,
    backgroundColor: SEARCE_COLORS.white,
    overflow: 'hidden',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    ...(elevation === 0 ? { boxShadow: 'none' } : {}),
  };

  const tableContainerBaseSx = {
    ...TABLE_STYLES.tableContainer,
    maxHeight,
    overflow: 'auto',
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    minHeight: loading || empty ? 200 : 0,
    flex: '1 1 auto',
    border: `1px solid ${alpha(SEARCE_COLORS.border, 0.85)}`,
    borderRadius: 1,
    backgroundColor: SEARCE_COLORS.white,
  };

  return (
    <Paper
      elevation={elevation}
      sx={
        paperSx != null
          ? [paperBaseSx, paperSx as any]
          : paperBaseSx
      }
    >
      {showHeader && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: title != null ? 'space-between' : 'flex-end',
            alignItems: 'center',
            mb: dense ? 1 : 1.5,
            width: '100%',
            minWidth: 0,
            ...(dense ? TABLE_STYLES.tableToolbarTopDense : TABLE_STYLES.tableToolbarTop),
            ...(headerBg != null && { backgroundColor: headerBg }),
          }}
        >
          {title != null && (
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: headerText,
                fontSize: '1.025rem',
              }}
            >
              {title}
            </Typography>
          )}
          {headerAction != null && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: headerText,
                flexShrink: 0,
                minWidth: 0,
                ...(title == null ? { width: '100%', justifyContent: 'flex-end' } : {}),
              }}
            >
              {headerAction}
            </Box>
          )}
        </Box>
      )}

      <TableContainer
        sx={
          tableContainerSx != null
            ? [tableContainerBaseSx, tableContainerSx as any]
            : tableContainerBaseSx
        }
      >
        {loading && <TableLoadingOverlay />}

        {empty && !loading && (
          <Table size={TABLE_STYLES.tableSize}>
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={100}
                  align="center"
                  sx={{
                    ...TABLE_STYLES.cellEmpty,
                    borderBottom: 'none',
                    verticalAlign: 'middle',
                  }}
                >
                  <EmptyState title={emptyMessage} compact />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}

        {showTableContent && children}
      </TableContainer>

      {showPagination && (
        <TablePagination
          component="div"
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={rowsPerPageOptions}
          sx={
            paginationSx != null
              ? [
                  {
                    flexShrink: 0,
                    width: '100%',
                    maxWidth: '100%',
                    borderTop: `1px solid ${alpha(SEARCE_COLORS.border, 0.9)}`,
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      color: paginationText,
                      fontWeight: 500,
                    },
                    '& .MuiTablePagination-select': {
                      color: paginationActive,
                      fontWeight: 600,
                    },
                    '& .MuiIconButton-root': {
                      color: paginationActive,
                      '&:hover': {
                        backgroundColor: alpha(paginationActive, 0.1),
                      },
                    },
                  },
                  paginationSx as any,
                ]
              : {
                  flexShrink: 0,
                  width: '100%',
                  maxWidth: '100%',
                  borderTop: `1px solid ${alpha(SEARCE_COLORS.border, 0.9)}`,
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    color: paginationText,
                    fontWeight: 500,
                  },
                  '& .MuiTablePagination-select': {
                    color: paginationActive,
                    fontWeight: 600,
                  },
                  '& .MuiIconButton-root': {
                    color: paginationActive,
                    '&:hover': {
                      backgroundColor: alpha(paginationActive, 0.1),
                    },
                  },
                }
          }
        />
      )}
    </Paper>
  );
};

export default CommonTable;
